import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Row, Space, Statistic, Tag, Typography } from 'antd';
import {
  ApiOutlined,
  CalendarOutlined,
  DashboardOutlined,
  FileTextOutlined,
  GoldOutlined,
  SettingOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';

const { Paragraph, Text } = Typography;

const HUB_LINKS = [
  {
    path: '/asset/workshop',
    title: 'Workshop / Garage',
    desc: 'Intake, holds, parts, labour, QC and release for maintenance cases',
    icon: <ToolOutlined />,
  },
  {
    path: '/asset/pmv/control-tower',
    title: 'Command Center',
    desc: 'What needs attention across the fleet today',
    icon: <DashboardOutlined />,
  },
  {
    path: '/asset/pmv',
    title: 'Fleet Register',
    desc: 'Plant, machinery and vehicle units',
    icon: <GoldOutlined />,
  },
  {
    path: '/asset/pmv/operations',
    title: 'Deployment Control',
    desc: 'Requests, reservations, transit and site presence',
    icon: <ToolOutlined />,
  },
  {
    path: '/asset/pmv/daily-log-sheets',
    title: 'Daily Log Sheets (Bulk)',
    desc: 'Site/shift bulk capture for hundreds of equipment — Wave 5.5',
    icon: <CalendarOutlined />,
  },
  {
    path: '/asset/pmv/daily-logs',
    title: 'Daily Logs',
    desc: 'Individual equipment daily logs, meters and evidence',
    icon: <CalendarOutlined />,
  },
  {
    path: '/asset/pmv/hire/control',
    title: 'Hired / Rented Equipment',
    desc: 'Hire control, agreements, timesheets, statements and invoice recon',
    icon: <GoldOutlined />,
  },
  {
    path: '/asset/pmv/hire/agreements',
    title: 'Hire Agreements',
    desc: 'Commercial terms and rates for rented/leased units',
    icon: <FileTextOutlined />,
  },
  {
    path: '/asset/pmv/components',
    title: 'Components',
    desc: 'Tyres, batteries and installable components',
    icon: <ToolOutlined />,
  },
  {
    path: '/asset/pmv/fuel/control-tower',
    title: 'Fuel Control',
    desc: 'Fuel attribution, sources and reconciliation',
    icon: <DashboardOutlined />,
  },
  {
    path: '/asset/pmv/reliability/control-tower',
    title: 'Reliability',
    desc: 'Health, failures, forecasts and attention',
    icon: <ToolOutlined />,
  },
  {
    path: '/asset/pmv/connected',
    title: 'Connected Fleet',
    desc: 'Devices, providers, connectivity and data quality (not a live map)',
    icon: <ApiOutlined />,
  },
  {
    path: '/asset/pmv/requests',
    title: 'Equipment Requests',
    desc: 'Capability demand, matching and fulfilment',
    icon: <FileTextOutlined />,
  },
  {
    path: '/asset/pmv/setup',
    title: 'Configuration',
    desc: 'Classifications, inspections, documents and hire rates',
    icon: <SettingOutlined />,
  },
];

const PmvHubPage: React.FC = () => {
  const navigate = useNavigate();
  const today = dayjs().format('YYYY-MM-DD');

  const summaryQ = useQuery({
    queryKey: ['pmv-hub-operations-summary'],
    queryFn: () => pmvService.getOperationsSummary(),
    staleTime: 30_000,
    retry: false,
  });

  const dailyQ = useQuery({
    queryKey: ['pmv-hub-operations-daily', today],
    queryFn: () => pmvService.getOperationsDailySummary(today),
    staleTime: 30_000,
    retry: false,
  });

  const towerQ = useQuery({
    queryKey: ['pmv-control-tower', today],
    queryFn: () => pmvService.getControlTower({ operating_date: today, publish_signals: false }),
    staleTime: 30_000,
    retry: false,
  });

  const s = summaryQ.data ?? {};
  const d = dailyQ.data;
  const missing = d?.missingLogs ?? 0;
  const breakdowns = towerQ.data?.open_breakdowns ?? 0;

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Plant, Machinery & Vehicles"
          documentNumber=""
          statusTag={<Tag color="blue">Command Center</Tag>}
          primaryActions={
            <Button type="primary" onClick={() => navigate('/asset/pmv/control-tower')}>
              Open Command Center
            </Button>
          }
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Operational entry for plant and fleet work. Start with attention, then move to deployment,
            field operations, or a unit passport.
          </Paragraph>
        </div>

        {(missing > 0 || breakdowns > 0) && (
          <Alert
            type="warning"
            showIcon
            message="Attention required"
            description={
              [
                missing > 0 ? `${missing} daily log${missing === 1 ? '' : 's'} missing today` : null,
                breakdowns > 0 ? `${breakdowns} open breakdown${breakdowns === 1 ? '' : 's'}` : null,
              ]
                .filter(Boolean)
                .join(' · ')
            }
            action={
              <Button size="small" type="primary" onClick={() => navigate('/asset/pmv/control-tower')}>
                Review
              </Button>
            }
          />
        )}

        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <Card size="small" hoverable onClick={() => navigate('/asset/pmv/requests')}>
              <Statistic title="Open requests" value={s.submittedRequests ?? 0} loading={summaryQ.isLoading} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" hoverable onClick={() => navigate('/asset/pmv/operations')}>
              <Statistic title="Deployed" value={s.deployed ?? 0} loading={summaryQ.isLoading} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" hoverable onClick={() => navigate('/asset/pmv/daily-log-sheets')}>
              <Statistic title="Logs certified today" value={d?.logsCertified ?? 0} loading={dailyQ.isLoading} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" hoverable onClick={() => navigate('/asset/pmv/daily-log-sheets')}>
              <Statistic
                title="Missing logs today"
                value={missing}
                loading={dailyQ.isLoading}
                valueStyle={missing ? { color: '#cf1322' } : undefined}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {HUB_LINKS.map((link) => (
            <Col xs={24} sm={12} lg={8} key={link.path}>
              <Card
                size="small"
                hoverable
                onClick={() => navigate(link.path)}
                style={{ cursor: 'pointer', height: '100%' }}
              >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Space>
                    {link.icon}
                    <Text strong>{link.title}</Text>
                  </Space>
                  <Text type="secondary">{link.desc}</Text>
                  <Button type="link" style={{ padding: 0 }}>
                    Open
                  </Button>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Space>
    </WorkspaceLayout>
  );
};

export default PmvHubPage;
